import { VIDEO_STYLES, showreelCount } from "./scene.js";
import { FORMAT_SIZE, drawnStyle, showreelVideo } from "./brief.js";
import { animationName } from "./animations.js";
import { POST_MS, type Motion } from "./script.js";
import { applyMargin, DEFAULT_MARGIN_BPS, usdToMicro } from "./money.js";
import type { BriefDraft } from "./brief.js";
import type { Operation } from "./provider.js";

/**
 * What pressing Generate is about to do, and what it will cost, said in words before anything runs (03).
 *
 * It lives here rather than in the browser because two things have to agree about it: the screen that shows the
 * estimate and the worker that will actually run the graph. The shape of the plan is the shape of the pipeline —
 * script, a picture per shot, motion, voice, captions, render — so a step added to one has to be added here,
 * where the omission is visible.
 *
 * The costs are a planning table of their own (04). **They are not the truth**: the real ones live in `pricing.json` beside
 * the adapters and move when a vendor moves. Before this estimate is shown to anyone paying, the API has to return
 * it — the number a customer reads must come from the same table the charge will (docs/14). Until then a test holds
 * this table to that one wherever a step is priced by the unit: a picture, a clip (`plan.test.ts`).
 *
 * What a step costs Joogoos at the vendor, in dollars, and not what it is charged: that is `PLANNED_PRICES`, below.
 * Until 2026-10-02 this held charges with the 40% margin folded in, so the plan could not follow the margin when the
 * owner took it to 20%, and two of its prices were under the vendor's bill before any margin at all.
 */
export const PLANNED_COSTS: Partial<Record<Operation, number>> = {
  // Measured on the dev box on 2026-09-25: $0.09 charged for a script since the scene library joined its prompt, at the
  // 40% then, so $0.0643 of cost. This said $0.02 before, and the plan showed a drawn video at half what it cost.
  write_script: 0.0643,
  // fal's Nano Banana 2, edit and generate alike: $0.08 an image at 1K, 1.5× at 2K and 2× at 4K (fal.ai, read
  // 2026-10-02). This said $0.039 and the catalogue $0.06, both under it, so every picture was sold below its cost.
  edit_image: 0.08,
  generate_image: 0.08,
  // A clip is billed by the second, in clips of 5 or 10 (Kling 2.6 Pro, $0.07 a second at the vendor): this is 5
  // seconds. It said half of it until 2026-09-26, while every clip bought was at least five seconds.
  image_to_video: 0.35,
  // One shot's line. It said $0.006 charged, at 40% and the $0.12 per 1,000 characters the catalogue had for ElevenLabs
  // Multilingual v2: about 36 characters. fal bills those $0.10 per 1,000 (read 2026-10-02), so $0.0036.
  tts: 0.0036,
  // These three were charges at 40% — $0.002, $0.045 and $0.0008 — and are those divided back by 1.4, rounded up.
  align: 0.0015,
  render: 0.0322,
  describe_image: 0.0006,
};

/**
 * What the plan says a step is charged: its cost with the margin, worked out as the charge is (`applyMargin`: in
 * micro-dollars, rounded up), so the plan and the bill agree to the micro on a step that costs what the table says.
 * The default margin, because the plan is drawn in the browser, which cannot read the box's, and no box sets one.
 */
export const PLANNED_PRICES: Partial<Record<Operation, number>> = Object.fromEntries(
  Object.entries(PLANNED_COSTS).map(([op, usd]) => [op, Number(applyMargin(usdToMicro(String(usd)), DEFAULT_MARGIN_BPS)) / 1_000_000]),
);

/**
 * What a render is reserved at before it runs: 200 micro-dollars a second of video, metered by processor time
 * afterwards. One definition, because the render worker reserves it and the post's cap, set when its script is
 * written, has to have room for it (2026-09-25: a drawn video with no voice was capped at its script alone, and its
 * render was refused).
 */
export const renderEstimateMicro = (durationMs: number): bigint => BigInt(Math.ceil(durationMs / 1000)) * 200n;

/**
 * The voices on offer. One catalogue, so the chooser and the composition name the same thing. The name is ours; the
 * voice is the ElevenLabs one beside it, which is what the speech call sends. Until 2026-09-25 the call sent our name,
 * which ElevenLabs does not know, and every narrated video failed; the accents were corrected the same day to what the
 * voices are, since ElevenLabs has no stock Scottish or Welsh voice.
 */
export const VOICES: ReadonlyArray<{ id: string; name: string; accent: string; note: string; elevenlabs: string }> = [
  { id: "isla", name: "Isla", accent: "English", note: "warm, unhurried", elevenlabs: "Lily" },
  { id: "ruth", name: "Ruth", accent: "English", note: "calm, confident", elevenlabs: "Alice" },
  { id: "theo", name: "Theo", accent: "English", note: "clear, measured", elevenlabs: "Daniel" },
  { id: "ada", name: "Ada", accent: "American", note: "warm, friendly", elevenlabs: "Matilda" },
  { id: "mo", name: "Mo", accent: "English", note: "warm, steady", elevenlabs: "George" },
  { id: "june", name: "June", accent: "American", note: "bright, upbeat", elevenlabs: "Jessica" },
];

export const voiceName = (id: string | undefined) => VOICES.find((v) => v.id === id)?.name ?? "a voice";

/** One line of the plan: a step of the pipeline, what it does, and what it costs. */
export interface PlanStep {
  key: string;
  title: string;
  detail: string;
  usd: number;
}

export interface Plan {
  steps: PlanStep[];
  /** How many shots the script is expected to come back with. One, for a post. */
  shots: number;
  totalUsd: string;
}

/** Fifteen seconds is four shots; the script decides for real, and the plan says "about" for exactly that reason. */
const SHOTS_FOR = (durationMs: number) => Math.max(3, Math.min(6, Math.round(durationMs / 3600)));

/** The longest clip a picture can be animated into, and the most photos one video is made from. */
export const CLIP_MAX_MS = 10_000, PHOTOS_MAX = 6;
/** The most shots of one video that are animated by the video model; the others are moved by a camera, for nothing. */
export const ANIMATED_MAX = 2;
/** What a clip of this length costs: it is bought in clips of five or ten seconds. */
const clipUsd = (ms: number) => price("image_to_video") * (Math.min(ms, CLIP_MAX_MS) <= 5_000 ? 1 : 2);

const price = (op: Operation, times = 1) => (PLANNED_PRICES[op] ?? 0) * times;

/**
 * Whether a photo piece may show a photo the owner chose for the video (2026-09-29): one they took, uploaded or kept
 * from their lookup, still kept. Never one made for them ("From your posts"), found for them (stock) or their logo:
 * a photo piece is the owner's proof, and a picture that is not theirs proves nothing. Here because the worker gives
 * the script only these and the plan counts only these, and the two have to agree (review, 2026-09-29).
 */
export const photoPieceMay = (m: { kind: string; origin: string; tags: readonly string[]; deletedAt?: Date | string | null }) =>
  m.kind === "image" && !m.deletedAt && m.origin !== "generated" && m.origin !== "library" && !m.tags.includes("logo");

/** A photo as the plan needs to know it: what a screen's list of the owner's photos already says of each. */
export type PlanPhoto = { id: string; kind: string; origin: string; tags: readonly string[] };

/**
 * How many of the photos chosen for a video of pieces it may show (review, 2026-09-29): only those a photo piece may
 * show (`photoPieceMay`), each in one piece at most, and no more than the pieces its length has between its opening
 * hero and its close. The plan said "your 6 photos" of a 10-second video with room for one, and counted photos made
 * for the owner that no photo piece shows. `library` is the owner's photos as a screen has them, so one made for
 * them, gone or never theirs is not counted; without it every photo chosen is. Nothing but a video of pieces, in any
 * of the piece styles (ADR 0061), shows one.
 */
export function reelPhotos(draft: BriefDraft, library?: readonly PlanPhoto[]): { chosen: number; shown: number } {
  if (!showreelVideo(draft)) return { chosen: 0, shown: 0 };
  const ids = draft.sourceMediaIds ?? [];
  const chosen = library ? ids.filter((id) => library.some((m) => m.id === id && photoPieceMay(m))).length : ids.length;
  return { chosen, shown: Math.max(0, Math.min(chosen, PHOTOS_MAX, showreelCount(draft.durationMs ?? 15_000).most - 2)) };
}

/** Those photos as the plan and its sentence say them, never more than may be shown, or null when none may be. */
export function reelPhotosSaid({ chosen, shown }: { chosen: number; shown: number }): string | null {
  if (!shown) return null;
  if (shown === 1) return chosen === 1 ? "your photo where the story shows it" : "one of your photos where the story shows it";
  return `up to ${shown} of your photos where the story shows them`;
}

/**
 * The plan for a draft. Missing answers are read the way generation would read them: no photos means the pictures
 * are generated rather than edited, and no voice means no narration. `library` is the owner's photos, when the screen
 * has them, so a video of pieces counts only those it may show (`reelPhotos`); no price depends on it.
 */
export function planFor(draft: BriefDraft, library?: readonly PlanPhoto[]): Plan {
  const own = draft.sourceMediaIds?.length ?? 0;
  const steps: PlanStep[] = [];

  // The size it is laid out at is its shape's (`FORMAT_SIZE`): the plan said 1080×1920 for every render, a story's, and
  // the Studio's render node the same, over a square post (the first pilot round, 2026-10-05).
  const size = FORMAT_SIZE[draft.format ?? "story"];
  const sized = `${size.width}×${size.height}`;

  if (draft.kind === "post") {
    // A post runs the same graph as a video with one shot: a script, a picture, and a render that lays the words out.
    // Leaving the script and the render off made the plan cheaper than the charge. A post has no music: it is a picture.
    // One shot is what is made, whatever the model writes (`asPost` in the worker). With no photo, the model may draw
    // the picture as a card when a number or a document says it better (03 §the brief), which costs nothing to make:
    // the plan says so, so a drawn post is not a surprise after one generated picture was shown and priced.
    // A photo chosen for a post is not shown as it is: the script writes what to change and an image model changes it
    // (`edit_image`, `shotImage` in the worker). "From the photo you chose" read as the photo itself (2026-10-12). And a
    // post is rendered like a video, its one shot held for `POST_MS`, which no screen said.
    steps.push({ key: "script", title: "Write the words", detail: "one picture's worth, from your idea", usd: price("write_script") });
    steps.push({
      key: "picture", title: "One picture",
      detail: own ? "your photo, changed by an image model from what the script asks of it, with your words drawn on top"
        : "generated, with your words drawn on top, or drawn as a card for nothing when a number or a document says it better",
      usd: price(own ? "edit_image" : "generate_image"),
    });
    steps.push({ key: "compose", title: "Lay out the words", detail: `the text is drawn, never generated, into a ${POST_MS / 1000}-second clip, ${sized}`, usd: price("render") });
    return { steps, shots: 1, totalUsd: total(steps) };
  }

  const shots = draft.look !== "drawn" && own ? Math.min(own, PHOTOS_MAX) : SHOTS_FOR(draft.durationMs ?? 15_000);
  if (draft.look === "drawn") {
    // Drawn by the renderer from its own library: no picture to make and no clip to animate, so nothing to charge
    // until the voice and the render (ADR 0050). A video of pieces shows the owner's photos inside its photo pieces
    // (2026-09-29), as they are and read once on upload, so they cost nothing either; no style but the piece styles
    // shows one.
    const shown = reelPhotosSaid(reelPhotos(draft, library));
    steps.push({ key: "script", title: "Write the script", detail: `about ${shots} scenes, their words and the order`, usd: price("write_script") });
    steps.push({ key: "pictures", title: `${shots} drawn scenes`, usd: 0, detail: shown ? `drawn in your colours, with ${shown}` : "drawn in your colours, each one moving on its own" });
  } else if (own) {
    // The photos the owner chose are what the video is made of (2026-09-26): one shot each and nothing invented beside
    // them. "Pick a few, or none and I'll generate" promised exactly that, and one photo came back planned as three
    // shots, two of them generated. Each is toured by our own camera over what was read in it (2026-09-27, `tour.ts`):
    // no picture to make and no clip to buy, so nothing to pay but the words and the render.
    const n = shots;
    steps.push({ key: "script", title: "Write the script", detail: n === 1 ? "one shot, the words for it" : `${n} shots, one for each photo, the words and the order`, usd: price("write_script") });
    steps.push({ key: "pictures", title: n === 1 ? "Your photo" : `Your ${n} photos`, detail: n === 1 ? "as it is, nothing added to it" : "as they are, nothing added to them", usd: 0 });
    steps.push({ key: "motion", title: "Toured by our camera", detail: `it flies to what matters in ${n === 1 ? "the photo" : "each photo"}, and draws on it`, usd: 0 });
  } else {
    // Invented pictures, two of them animated and the rest moved by a slow camera, as the script is told to.
    const moving = Math.min(ANIMATED_MAX, shots);
    steps.push({ key: "script", title: "Write the script", detail: `about ${shots} shots, the words and the order`, usd: price("write_script") });
    steps.push({ key: "pictures", title: `${shots} pictures`, detail: "all generated, because no photos were chosen", usd: price("generate_image", shots) });
    steps.push({ key: "motion", title: `${moving} clips`, detail: "two pictures animated, the others moved by a slow camera", usd: clipUsd((draft.durationMs ?? 15_000) / shots) * moving });
  }
  // A video of pieces has no captions: its type is the caption, so its voice says the words on screen, still timed word
  // by word (dev box, 2026-09-28; every piece style since 2026-09-29). With no voice there are no captions in any
  // style: nothing is spoken to caption.
  if (draft.voice?.enabled) {
    steps.push({
      key: "voice", title: "Narration",
      detail: `${voiceName(draft.voice.voiceId)}, ${showreelVideo(draft) ? "saying the words on screen, timed word by word" : "with captions aligned word by word"}`,
      usd: price("tts", shots) + price("align", shots),
    });
  } else {
    steps.push({ key: "voice", title: "No narration", detail: "nothing is spoken, the words on screen carry it", usd: 0 });
  }
  // A drawn video of pieces opens on one, which an animation is never laid over (ADR 0058): not promised, then.
  if (draft.animation?.enabled && !showreelVideo(draft)) {
    // Drawn by the renderer from a file we already have: no model, so nothing to charge (ADR 0032).
    steps.push({ key: "animation", title: "Animation", detail: `${animationName(draft.animation.animationId)}, over the opening shot`, usd: 0 });
  }
  if (draft.music !== false) {
    // Drawn by code in the worker, like the scenes: nothing to charge (ADR 0054).
    const feel = VIDEO_STYLES.find((s) => s.id === (drawnStyle(draft) ?? "clean"))!.name;
    steps.push({ key: "music", title: "Music", detail: `composed for this video, in the ${feel} style's feel${draft.voice?.enabled ? ", quieter under the voice" : ""}`, usd: 0 });
  }
  steps.push({ key: "render", title: "Render it", detail: `${sized}, metered by processor time`, usd: price("render") });
  return { steps, shots, totalUsd: total(steps) };
}

const total = (steps: PlanStep[]) => steps.reduce((n, s) => n + s.usd, 0).toFixed(2);

/** A shot as a rerun needs to know it: what the route reads from its row and the editor from its view. */
export type RerunShot = { scene: unknown; motion: Motion; dialogue: string | null; isNarration: boolean; sourceMediaId: string | null; durationMs: number };

/**
 * What rerunning one shot makes again, and about what it costs, priced as the plan prices those steps (2026-10-12).
 * One definition, because the route decides by it and the editor words and prices its button by it: Rerun was the one
 * paid press with no number on it, and a toured photo took an instruction that nothing used.
 *
 * A picture is made again only for a shot whose picture a model made: a drawn scene is made from its words (ADR 0050)
 * and a toured photo is the owner's own, as it is (`tour.ts`). A voice only for a shot whose line is the voice's. A
 * clip follows a picture animated by the video model, and the render follows anything.
 */
export function shotRerun(shot: RerunShot): { picture: boolean; voice: boolean; usd: number } {
  const voice = !!shot.dialogue && shot.isNarration;
  const picture = !shot.scene && shot.motion !== "tour";
  const usd = (picture ? price(shot.sourceMediaId ? "edit_image" : "generate_image") + (shot.motion === "image_to_video" ? clipUsd(shot.durationMs) : 0) : 0)
    + (voice ? price("tts") + price("align") : 0)
    + (picture || voice ? price("render") : 0);
  return { picture, voice, usd };
}

/* ── whether it can be paid for, before the button ──────────────────────────────────────────────────────────── */

/**
 * Whether this plan can be paid for, checked **before** the Generate button rather than mid-pipeline
 * ([ADR 0012](../../../docs/adr/0012-pay-as-you-go-with-a-monthly-cap.md) point 2).
 *
 * `runCall` keeps every one of these checks as its backstop, and has to: an estimate is an estimate, and a rerun in
 * the middle of a run was never covered by the sum taken at the start. But a half-run pipeline that stops at a
 * ceiling is a bad way to find out about the ceiling, and the fix is arithmetic somebody can read before they press.
 *
 * In dollars and floats, not micro-dollars, because both sides of it are already display strings and the decision
 * is about whether $0.85 fits under $2.00 — a cent either way changes nothing a customer would notice. The money
 * that moves is still bigint everywhere it matters (ADR 0002).
 */
export type Affordability =
  | { ok: true }
  /** The person's own ceiling. Not a failure, and the screen opens the two-door dialog rather than an error. */
  | { ok: false; reason: "monthly_cap"; leftUsd: string }
  | { ok: false; reason: "daily_cap"; leftUsd: string }
  /** No money, which no cap can fix: the only one of the three whose answer is a top-up. */
  | { ok: false; reason: "credit"; leftUsd: string };

export function canAfford(planTotalUsd: string, money: {
  balanceUsd: string;
  dayUsd?: string | undefined;
  dailyCapUsd?: string | undefined;
  monthUsd?: string | undefined;
  monthlyCapUsd?: string | null | undefined;
}): Affordability {
  const need = Number(planTotalUsd);
  const left = (total: string | undefined, cap: string | null | undefined) =>
    cap == null || cap === "" ? null : Math.max(0, Number(cap) - Number(total ?? 0));

  // Credit first, because it is the one a cap cannot explain away: a person with 40 cents and a $50 ceiling is not
  // being stopped by the ceiling.
  const balance = Number(money.balanceUsd);
  if (need > balance) return { ok: false, reason: "credit", leftUsd: balance.toFixed(2) };

  const month = left(money.monthUsd, money.monthlyCapUsd);
  if (month != null && need > month) return { ok: false, reason: "monthly_cap", leftUsd: month.toFixed(2) };

  const day = left(money.dayUsd, money.dailyCapUsd);
  if (day != null && need > day) return { ok: false, reason: "daily_cap", leftUsd: day.toFixed(2) };

  return { ok: true };
}

/** What the plan says under its total: the month against the cap, or nothing when no cap is set. */
export function monthAgainstCap(monthUsd: string | undefined, monthlyCapUsd: string | null | undefined): string | null {
  if (monthlyCapUsd == null) return null;
  return `this month: $${Number(monthUsd ?? 0).toFixed(2)} of $${Number(monthlyCapUsd).toFixed(2)}`;
}
